/**
 * The thing that travels the route.
 *
 * Generalised from `DispatchPlate`'s `--fx-park`. Under reduced motion the token
 * does not vanish and it does not sit at the start — it **parks** a third of the
 * way along the path. A dot at the origin says "nothing has happened"; a dot at
 * the destination says "this is finished"; a dot in transit says the same thing
 * the animation says, and says it without moving. That is the whole idea: the
 * meaning survives the media query rather than being carried by the motion.
 *
 * Both branches position through `offset-path`, so the parked token and the
 * moving one are placed by the same geometry and cannot drift apart.
 */
import { m } from 'motion/react'

import { useReducedMotionSafe } from './motion'

const PARKED_AT = '34%'

export function Token({
  d,
  delay = 0,
  duration = 1.4,
  r = 3.5,
  className = 'kl-token',
  repeatDelay = 0.9,
}: {
  /** The path data the token rides — the same string the visible stroke uses. */
  d: string
  delay?: number
  duration?: number
  r?: number
  className?: string
  repeatDelay?: number
}) {
  const reduced = useReducedMotionSafe()
  const offsetPath = `path('${d}')`

  if (reduced) {
    return <circle r={r} className={className} style={{ offsetPath, offsetDistance: PARKED_AT }} />
  }

  return (
    <m.circle
      r={r}
      className={className}
      style={{ offsetPath }}
      initial={{ offsetDistance: '0%' }}
      whileInView={{ offsetDistance: '100%' }}
      viewport={{ once: true, margin: '-10%' }}
      transition={{ duration, delay, repeat: Infinity, repeatDelay, ease: 'easeInOut' }}
    />
  )
}
